import { useState } from 'react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
  dashboardTableDialogEmbeddedSurfaceClassName,
  dashboardTableDialogEmbeddedTableClassName,
} from '@shigo-ui/dashboard-ui';

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../../lib/messages';
import {
  isPlacementSortProperty,
  placementPageSizes,
  PlacementSortDirection,
  PlacementSortProperty,
  PlacementStatus,
  resolvePlacementStatusLabel,
  type PlacementsFilter,
} from '../../../../../supply/placements/model';
import {
  usePlacementsQuery,
  usePlacementStatusCountsQuery,
} from '../../../../../supply/placements/hooks';
import { buildIntegrationPlacementsDialogColumns } from '../integrationPlacementsDialogColumns';

export function IntegrationPlacementsDialogTable(props: {
  integrationId: string;
  tableLabel?: string;
}) {
  const [filter, setFilter] = useState<PlacementsFilter>({
    searchTerm: '',
    statuses: [PlacementStatus.Active],
    types: [],
    demandIntegrationId: props.integrationId,
    publishersIds: [],
    page: 0,
    size: 10,
    sortProperty: PlacementSortProperty.UpdatedOn,
    sortDirection: PlacementSortDirection.Desc,
  });
  const query = usePlacementsQuery(filter);
  const counts = usePlacementStatusCountsQuery(filter);
  const search = useDebouncedValue(
    filter.searchTerm,
    (searchTerm) => {
      setFilter((current) => ({ ...current, searchTerm, page: 0 }));
    },
    650,
  );
  const columns = buildIntegrationPlacementsDialogColumns();
  const statusLabel = filter.statuses.length
    ? filter.statuses.map(resolvePlacementStatusLabel).join(', ')
    : dashboardT('placements.allStatuses');

  return (
    <DashboardDataTable
      tableLabel={props.tableLabel ?? dashboardT('integrations.placementsTitle')}
      columns={columns}
      items={query.data?.content ?? []}
      getRowKey={(placement) => placement.id}
      loading={query.isLoading}
      loadingMessage={dashboardT('integrations.placementsLoading')}
      errorMessage={query.isError ? dashboardT('integrations.placementsError') : undefined}
      emptyMessage={dashboardT('integrations.placementsEmpty')}
      minWidthClassName="min-w-[720px]"
      className={dashboardTableDialogEmbeddedTableClassName}
      surfaceClassName={dashboardTableDialogEmbeddedSurfaceClassName}
      toolbar={
        <>
          <DashboardTableSearchInput
            aria-label={dashboardT('placements.search')}
            placeholder={dashboardT('placements.search')}
            value={search.draft}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
          />
          <DashboardTableMultiSelectFilter
            ariaLabel={dashboardT('placements.statusFilter')}
            label={statusLabel}
            value={filter.statuses}
            options={Object.values(PlacementStatus).map((status) => ({
              value: status,
              label: resolvePlacementStatusLabel(status),
              count: counts.data?.[status],
            }))}
            onChange={(statuses) => {
              setFilter((current) => ({ ...current, statuses, page: 0 }));
            }}
          />
        </>
      }
      pagination={{
        value: {
          page: filter.page,
          pageSize: filter.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: placementPageSizes,
        disabled: query.isFetching,
        onChange: ({ page, pageSize }) => {
          setFilter((current) => ({
            ...current,
            page: pageSize === current.size ? page : 0,
            size: pageSize,
          }));
        },
      }}
      sort={{
        value: filter.sortProperty
          ? {
              key: filter.sortProperty,
              direction:
                filter.sortDirection === PlacementSortDirection.Asc
                  ? DashboardDataTableSortDirection.Asc
                  : DashboardDataTableSortDirection.Desc,
            }
          : undefined,
        onChange: (next) => {
          if (!next) {
            setFilter((current) => ({ ...current, sortProperty: null, page: 0 }));
            return;
          }
          if (!isPlacementSortProperty(next.key)) return;
          const sortProperty: PlacementSortProperty = next.key;
          setFilter((current) => ({
            ...current,
            sortProperty,
            sortDirection:
              next.direction === DashboardDataTableSortDirection.Asc
                ? PlacementSortDirection.Asc
                : PlacementSortDirection.Desc,
            page: 0,
          }));
        },
      }}
    />
  );
}
